import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { cn } from '@/admin/lib/utils';
import type { PublicShareItem } from '../lib/types';
import { formatVideoTime, useProgressBar } from './share-viewer-shared';

// ---------------------------------------------------------------------------
// Audio player hook
// ---------------------------------------------------------------------------

const PLAYBACK_RATES = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] as const;

function useAudioPlayer(audioRef: React.RefObject<HTMLAudioElement | null>) {
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [playbackRate, setPlaybackRateState] = useState(1);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;

    const syncDuration = () => {
      const d = a.duration;
      if (Number.isFinite(d) && d > 0) setDuration(d);
    };
    const syncBuffered = () => {
      if (a.buffered.length > 0) {
        setBuffered(a.buffered.end(a.buffered.length - 1));
      }
    };

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onTimeUpdate = () => {
      setCurrentTime(a.currentTime);
      syncDuration();
    };
    const onMeta = () => {
      syncDuration();
      setCurrentTime(a.currentTime);
    };
    const onProgress = () => {
      syncBuffered();
      syncDuration();
    };

    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('ended', onPause);
    a.addEventListener('timeupdate', onTimeUpdate);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('durationchange', onMeta);
    a.addEventListener('loadeddata', onMeta);
    a.addEventListener('canplay', onMeta);
    a.addEventListener('progress', onProgress);

    syncDuration();

    return () => {
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('ended', onPause);
      a.removeEventListener('timeupdate', onTimeUpdate);
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('durationchange', onMeta);
      a.removeEventListener('loadeddata', onMeta);
      a.removeEventListener('canplay', onMeta);
      a.removeEventListener('progress', onProgress);
    };
  }, [audioRef]);

  const togglePlay = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      void a.play();
    } else {
      a.pause();
    }
  }, [audioRef]);

  const seek = useCallback(
    (t: number) => {
      const a = audioRef.current;
      if (!a) return;
      a.currentTime = Math.max(0, Math.min(t, a.duration || 0));
    },
    [audioRef]
  );

  const setPlaybackRate = useCallback(
    (rate: number) => {
      const a = audioRef.current;
      if (!a) return;
      a.playbackRate = rate;
      setPlaybackRateState(rate);
    },
    [audioRef]
  );

  return {
    playing,
    currentTime,
    duration,
    buffered,
    playbackRate,
    togglePlay,
    seek,
    setPlaybackRate,
  };
}

// ---------------------------------------------------------------------------
// AudioView
// ---------------------------------------------------------------------------

const AUDIO_PLAYER_CARD_SHADOW =
  'shadow-[0_8px_5px_0_rgba(0,0,0,0.05),0_3px_3px_0_rgba(0,0,0,0.09),0_1px_2px_0_rgba(0,0,0,0.10)]';

export function AudioView(props: { item: PublicShareItem }): React.JSX.Element {
  const audioRef = useRef<HTMLAudioElement>(null);
  const player = useAudioPlayer(audioRef);
  const progress = useProgressBar(
    player.duration,
    player.seek,
    undefined,
    player.currentTime
  );

  const playedFraction =
    player.duration > 0 ? player.currentTime / player.duration : 0;
  const bufferedFraction =
    player.duration > 0 ? player.buffered / player.duration : 0;
  const displayProgress = progress.dragging
    ? progress.dragProgress
    : playedFraction;

  const effectiveTime = progress.dragging
    ? progress.dragProgress * player.duration
    : player.currentTime;
  const remaining =
    player.duration > 0 ? player.duration - effectiveTime : player.duration;
  const timeLabel =
    player.playing || player.currentTime > 0
      ? formatVideoTime(remaining)
      : formatVideoTime(player.duration);
  const handleRateChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      player.setPlaybackRate(Number(e.target.value));
    },
    [player]
  );

  return (
    <div className="flex w-full flex-1 items-center justify-center p-4 md:p-5">
      <div className="w-full max-w-md">
        <audio
          ref={audioRef}
          className="sr-only"
          src={props.item.content_url}
          preload="auto"
        >
          Your browser does not support audio playback.
        </audio>

        <div
          className={cn(
            'rounded-xl border border-[#ebebeb] bg-white px-2 py-2.5',
            AUDIO_PLAYER_CARD_SHADOW
          )}
        >
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => player.togglePlay()}
              className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#f5f5f5] text-[#1a1a1a] outline-none transition-colors hover:bg-[#ebebeb] focus-visible:ring-2 focus-visible:ring-[#0077ff]/40"
              aria-label={player.playing ? 'Pause' : 'Play'}
            >
              {player.playing ? (
                <Pause
                  className="size-4"
                  fill="currentColor"
                  stroke="none"
                  strokeWidth={0}
                  aria-hidden
                />
              ) : (
                <Play
                  className="size-4 translate-x-px"
                  fill="currentColor"
                  stroke="none"
                  strokeWidth={0}
                  aria-hidden
                />
              )}
            </button>

            <div className="flex min-w-0 flex-1 items-center gap-2">
              <div
                ref={progress.barRef}
                className="relative min-w-0 flex-1 cursor-pointer py-2"
                onClick={progress.handleBarClick}
                onPointerDown={progress.handlePointerDown}
                onKeyDown={progress.handleKeyDown}
                onMouseEnter={progress.handleMouseEnter}
                onMouseLeave={progress.handleMouseLeave}
                onMouseMove={progress.handleMouseMove}
                role="slider"
                aria-label="Seek"
                aria-valuemin={0}
                aria-valuenow={
                  player.duration > 0
                    ? Number((player.currentTime / player.duration).toFixed(4))
                    : 0
                }
                aria-valuemax={1}
                aria-valuetext={`${formatVideoTime(player.currentTime)} of ${formatVideoTime(player.duration)}`}
                tabIndex={0}
              >
                <div
                  className={cn(
                    'relative w-full rounded-full transition-[height] duration-150',
                    progress.hovering || progress.dragging
                      ? 'h-[5px]'
                      : 'h-[3px]'
                  )}
                >
                  <div className="absolute inset-0 rounded-full bg-[#ebebeb]" />
                  <div
                    className="absolute inset-y-0 left-0 rounded-full bg-[#d8d8d8]"
                    style={{ width: `${bufferedFraction * 100}%` }}
                  />
                  {progress.hovering && !progress.dragging && (
                    <div
                      className="absolute inset-y-0 left-0 rounded-full bg-[#0077ff]/15"
                      style={{ width: `${progress.hoverProgress * 100}%` }}
                    />
                  )}
                  <div
                    className="absolute inset-y-0 left-0 rounded-full bg-[#0077ff]"
                    style={{ width: `${displayProgress * 100}%` }}
                  />
                  <div
                    className={cn(
                      'absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_1px_3px_rgba(0,0,0,0.12)] transition-[width,height,opacity] duration-150',
                      progress.hovering || progress.dragging
                        ? 'size-[10px] opacity-100'
                        : 'size-0 opacity-0'
                    )}
                    style={{ left: `${displayProgress * 100}%` }}
                  />
                </div>
              </div>

              <span
                className="shrink-0 self-center min-w-13 text-right font-mono text-[11px] font-medium tabular-nums text-[#8a8a8a]"
                aria-label={timeLabel}
              >
                {timeLabel}
              </span>

              <select
                value={player.playbackRate}
                onChange={handleRateChange}
                className="shrink-0 cursor-pointer appearance-none rounded-md border border-[#ebebeb] bg-[#fafafa] px-1.5 py-1 text-center text-[11px] leading-none font-medium tabular-nums text-[#6a6a6a] outline-none transition-colors hover:border-[#d9d9d9] hover:text-[#1a1a1a] focus-visible:ring-2 focus-visible:ring-[#0077ff]/30"
                aria-label="Playback speed"
              >
                {PLAYBACK_RATES.map(rate => (
                  <option key={rate} value={rate}>
                    {rate}×
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
